import { EventEmitter } from '@angular/core';
import { ILookup } from '../interfaces/ILookup.interface';
import { CurrentUser } from '../services/current-user.service';
import { BehaviorSubject } from 'rxjs';
import { AdvanceFilterConstants } from '../constants/advance-filter.constant';

export class DataTableFilter {
    quickFilter = '';
    filters: Array<Filter>;
    selectFilters: Array<string>;
    orderBy?: Order;
    pager: Pager;
    placeholder?= 'Msg.QuickFilter';
    totalCountPlaceholder? = 'Admin.add_edit_user.Total';
    filterChangedEvent = new EventEmitter<DataTableFilter>();
    advanceFiltersInitializedEvent = new BehaviorSubject<boolean>(false);
    activeFilters: Array<Filter>;
    guid: string;

    constructor(private currentUser?: CurrentUser) {
        this.filters = new Array<Filter>();
        this.pager = new Pager();
        this.orderBy = new Order();
        this.selectFilters = new Array<string>();
    }

    subscribeToChange(target: any) {
        return this.filterChangedEvent.subscribe(target);
    }
    /**
     * Triggers fitlerTable method with pagination reset
     */
    emitChange() {
        this.filterChangedEvent.emit(this);
    }


    toQueryString(): string {
        let queryString = '';
           this.filters.filter(f => f.value).forEach(filter => {
            if (filter.type === FilterType.Date) {
              if (this.currentUser) {
                filter.value =  !!filter.value ? this.currentUser.dateToIsoFormat(filter.value, this.currentUser.dateFormats.display.dateInput, false) : null;
              } else {
                  throw new Error('Current user must be provided if filter of type date is used!');
              }
          }

          if (filter.type === FilterType.DateTime) {
            if (this.currentUser) {
              filter.value = !!filter.value ? this.currentUser.addTimeToDate(filter.value) : null;
              filter.value = !!filter.value ? this.currentUser.dateToIsoFormat(filter.value, this.currentUser.getDateTimeFormat(), false) : null;
            } else {
                throw new Error('Current user must be provided if filter of type date is used!');
            }
          }
          queryString += `${encodeURIComponent(`${filter.name}_${this.operatorToString(filter.operator)}`)}=${encodeURIComponent(filter.value)}&`;
        });

        if (this.quickFilter && this.quickFilter.length > 0) {
            queryString += `quickFilter=${encodeURIComponent(this.quickFilter)}&`;
        }

        if (this.orderBy && this.orderBy.name) {
            const sort = `${this.orderBy.direction === OrderDirection.ASC ? '+' : '-'}${this.orderBy.name}`;
            queryString += `sort=${encodeURIComponent(sort)}&`;
        }

        if (this.pager) {
            if (this.pager.currentPage >= 0) {
                queryString += `offset=${this.pager.currentPage}&`;
            }

            if (this.pager.itemsPerPage > 0) {
                queryString += `limit=${this.pager.itemsPerPage}&`;
            }
        }

        return queryString.slice(0, -1);
    }

    /**
     * Converts filters to OData query string. If filter/orderBy column name is different on API than defined on datatable columns, columnMappings should be used.
     * @param columnMappings Optional parameter. Key/value pairs used to define mapping between filter properties and expected filter values on API.
     * @example
     * ```
     * const columnMappings = {
     *   'permissionName': 'permissions.name'
     * };
     * const queryString = this.toODataQueryString(columnMappings);
     * ```
     */
  toODataQueryString(columnMappings?: any): string {
    let queryString = '';
    this.filters
    .filter(filter => filter.value || (filter.type === FilterType.Select &&  filter.value !== undefined && filter.value !== '')).forEach(filter => {
      if (filter.type === FilterType.Date) {
        if (this.currentUser) {
          filter.value = !!filter.value ?  this.currentUser.dateToIsoFormat(filter.value, this.currentUser.dateFormats.display.dateInput, false): null;
        } else {
          throw new Error('Current user must be provided if filter of type date is used!');
        }
      }

      if (filter.type === FilterType.DateTime) {
        if (this.currentUser) {
          filter.value = !!filter.value ? this.currentUser.addTimeToDate(filter.value) : null;
          filter.value = !!filter.value ?  this.currentUser.dateToIsoFormat(filter.value, this.currentUser.getDateTimeFormat(), false): null;
        } else {
          throw new Error('Current user must be provided if filter of type date is used!');
        }
      }

      const filterName = columnMappings && columnMappings[filter.name] ? columnMappings[filter.name] : filter.name;

      if (queryString.startsWith('$')) {
        queryString += ` and ${filterName} ${this.operatorToStringOData(filter.operator)} '${encodeURIComponent(filter.value)}'`;
      } else {
        queryString += `$filter=${filterName} ${this.operatorToStringOData(filter.operator)} '${encodeURIComponent(filter.value)}'`;
      }
    });

    if (queryString.startsWith('$') && this.selectFilters.length) {
      queryString += `&`;
    }
    this.selectFilters.forEach(selectFilter => {
      if (selectFilter) {
        if (queryString.includes('$select')) {
          queryString += `,${selectFilter}`;
        } else {          
          queryString += `$select=${selectFilter}`;
        }
      }
    });

    if (this.quickFilter && this.quickFilter.length) {
      const searchQueryString = `$search="${encodeURIComponent(this.quickFilter)}"`;
      if(queryString && queryString.length) {
        queryString +=`&${searchQueryString}`
      } else {
        queryString += searchQueryString;
      }
    }

    if (this.orderBy && this.orderBy.name) {
      const orderByName = columnMappings && columnMappings[this.orderBy.name] ? columnMappings[this.orderBy.name] : this.orderBy.name;
      const sort = `${orderByName} ${this.orderBy.direction === OrderDirection.ASC ? 'ASC' : 'DESC'}`;
      queryString += `&$orderby=${encodeURIComponent(sort)}`;
    }

    if (this.pager) {
      if (this.pager.currentPage >= 0 && this.pager.itemsPerPage > 0) {
        queryString += `&$skip=${this.pager.currentPage * this.pager.itemsPerPage}`;
        queryString += `&$top=${this.pager.itemsPerPage}`;
      }
    }

    return queryString;
  }

    initFilters(guid: string) {
      this.guid = guid;
        if (guid) {
            const localStorageFilters: Array<Filter> = JSON.parse(localStorage.getItem(AdvanceFilterConstants.advanceFilterLocalStorage) || null);
            if (localStorageFilters && localStorageFilters[guid]) {
              this.filters.forEach(filter => {
                const savedFilter: Filter = localStorageFilters[guid].find((x: Filter) => x.name === filter.name && x.operator === filter.operator);
                if (savedFilter) {
                  filter.value = savedFilter.value;
                  filter.valueTo = savedFilter.valueTo; // for Dates
                  filter.valueText = savedFilter.valueText; // for async select
                }
              });
            }
            if (localStorageFilters && localStorageFilters[`${guid}_${AdvanceFilterConstants.quickFilterSuffix}`]) {
                this.quickFilter = localStorageFilters[`${guid}_${AdvanceFilterConstants.quickFilterSuffix}`];
            }
            const sortInfo = JSON.parse(localStorage.getItem(AdvanceFilterConstants.orderBy)) || {};
            if (sortInfo[guid]) {
                this.orderBy.name = sortInfo[guid].name;
                this.orderBy.direction = sortInfo[guid].direction;
            }

            this.pager.itemsPerPage = +(localStorage.getItem(`${guid}_${AdvanceFilterConstants.pageSize}`)) || this.pager.itemsPerPage;

            this.advanceFiltersInitializedEvent.next(true);
          } else {
            this.advanceFiltersInitializedEvent.next(false);
            this.pager.itemsPerPage = 10;
        }  
    }

    applyFilter(saveAdvanceFilters = true, emitChanges = true) {
      this.activeFilters = [];
      if (emitChanges) {
        this.emitChange();
      }
  
      for (const filterItem of this.filters) {
        if (filterItem.value || filterItem.valueTo) {
          this.activeFilters.push(filterItem);
        }
      }
  
      if (saveAdvanceFilters) {
        this.saveAdvanceFilters();
      }
    }
  
    saveAdvanceFilters() {
      if (this.guid) {
        const activeFilters = JSON.parse(localStorage.getItem(AdvanceFilterConstants.advanceFilterLocalStorage)) || {};
        activeFilters[this.guid] = this.activeFilters;
        localStorage.setItem(AdvanceFilterConstants.advanceFilterLocalStorage, JSON.stringify(activeFilters));
      }
    }
  
    saveQuickFilter() {
      if (this.guid) {
        const activeFilters = JSON.parse(localStorage.getItem(AdvanceFilterConstants.advanceFilterLocalStorage)) || {};
        activeFilters[`${this.guid}_${AdvanceFilterConstants.quickFilterSuffix}`] = this.quickFilter;
        localStorage.setItem(AdvanceFilterConstants.advanceFilterLocalStorage, JSON.stringify(activeFilters));
      }
    }
  
    public clearAllFilters() {
      this.filters.forEach(filterItem => this.resetAdvanceFilter(filterItem));
      this.applyFilter();
    }

    resetAdvancedFilterByFilterName(filterName: string) {
      const filter = this.filters.find(filter => filter.name === filterName)
      if(!filter) {
        throw new Error(`Filter '${filterName}' does not exist.`); 
      }
      this.resetAdvanceFilter(filter);
      this.applyFilter();
    }

    resetAdvanceFilter(filter: Filter) {
      filter.value = '';
      filter.valueTo = '';
      filter.valueText = '';
    }

    private operatorToString(operator: FilterOperator): string {
        switch (operator) {
            case FilterOperator.Equal:
                return 'eq';
            case FilterOperator.Bigger:
                return 'gt';
            case FilterOperator.Smaller:
                return 'lt';
            case FilterOperator.BiggerOrEqual:
                return 'geq';
            case FilterOperator.SmallerOrEqual:
                return 'leq';
            case FilterOperator.NotEqual:
                return 'neq';
            case FilterOperator.In:
                return 'in';
            case FilterOperator.NotIn:
                return 'nin';
            case FilterOperator.Like:
                return 'like';
            default:
                return 'like';
        }
    }
  private operatorToStringOData(operator: FilterOperator): string {
    switch (operator) {
      case FilterOperator.Equal:
        return 'eq';
      case FilterOperator.Bigger:
        return 'gt';
      case FilterOperator.Smaller:
        return 'lt';
      case FilterOperator.BiggerOrEqual:
        return 'ge';
      case FilterOperator.SmallerOrEqual:
        return 'le';
      case FilterOperator.NotEqual:
        return 'ne';
      case FilterOperator.In:
        return 'in';
      case FilterOperator.NotIn:
        return 'nin';
      case FilterOperator.Like:
        return 'like';
      default:
        return 'like';
    }
  }
}



export class Filter {
    name: string;
    // Filter label, not needed in case of column filter on datatable
    label?: string; 
    // If type is date, currentUser has to be provided, in case of datatable filter default value is FilterType.Text
    type?: FilterType; 
    value?: string | null;
    valueTo?: string;

    // Used for dates
    operator?: FilterOperator;

    // sets filter disabled state
    isDisabled?: boolean;

    // Select/Multiselect specific
    // Text of current selection	
    valueText?: string;
    // Items
    source?: Array<ILookup<number | string, string>>;
    // Select/Multiselect specific end
}

export class Order {
    name: string;
    direction: OrderDirection;
}

export enum OrderDirection {
    ASC,
    DESC
}

export enum FilterType {
    Text,
    /** Date is displayed in datatable in user format, when filtering it is sent to server in ISO format
     * ISO: 2021-06-16T22:00:00.000Z
     */
    Date,
    DateTime,
    Select,
    SearchableSelect,
    Multiselect,
    Number
}

// Value in database, operator, value from filter
// example [DateModified] from Db, BiggerOrEqual, dateModified filter value - is used for date modified from filter
export enum FilterOperator {
    Equal,
    Bigger,
    Smaller,
    BiggerOrEqual,
    SmallerOrEqual,
    NotEqual,
    In,
    NotIn,
    Like
}

export class Pager {
    currentPage = 0;
    itemsPerPage = 10;
}

export class RowChanges {
    public modifiedRows: Array<any>;
    public lastModifiedProperty: string;
    public lastModifiedRow: any;
}
